import React from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'outline' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  children,
  className = '',
  disabled,
  ...props
}) => {
  const baseStyles = 'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus:ring-offset-bg-primary disabled:opacity-50 disabled:cursor-not-allowed';

  const variantStyles: Record<ButtonVariant, string> = {
    primary: 'bg-accent-primary hover:bg-accent-primary-hover text-white shadow-sm focus:ring-accent-primary',
    secondary: 'bg-accent-secondary hover:bg-accent-secondary-hover text-white shadow-sm focus:ring-accent-secondary',
    success: 'bg-status-success hover:bg-green-600 text-white shadow-sm focus:ring-status-success',
    danger: 'bg-status-danger hover:bg-red-600 text-white shadow-sm focus:ring-status-danger',
    outline: 'border border-border-default bg-surface-primary text-text-secondary hover:text-text-primary hover:bg-surface-hover focus:ring-accent-primary',
    ghost: 'text-text-secondary hover:text-text-primary hover:bg-surface-hover focus:ring-accent-primary',
  };

  const sizeStyles: Record<ButtonSize, string> = {
    sm: 'text-xs min-h-8 px-3 py-1.5 rounded-button',
    md: 'text-sm min-h-9 px-3.5 py-2 rounded-button',
    lg: 'text-sm min-h-11 px-5 py-2.5 rounded-button',
  };

  const widthStyles = fullWidth ? 'w-full' : '';

  return (
    <button
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthStyles} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <svg
          className="animate-spin h-4 w-4 shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {!isLoading && leftIcon && <span className="shrink-0">{leftIcon}</span>}
      {children}
      {!isLoading && rightIcon && <span className="shrink-0">{rightIcon}</span>}
    </button>
  );
};

export default Button;
